import { useCallback } from 'react';
import { useLocation } from 'react-router';
import { sprintf } from 'sprintf-js';

import type { ApiUser } from 'api/userEnvironment/types/ApiUserEnvironment';
import { Box } from 'components/Box/Box';
import { StyledButtonOrLink } from 'components/Button/Button.styled';
import { Icon } from 'components/Icon/Icon';
import { Text } from 'components/Text/Text';
import { ClientOnly } from 'containers/ClientOnly';
import type { ApiOrg } from 'modules/listing/api/org/types/ApiOrg';
import { ListingViewSidebarInfoBox } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebar.styled';
import { fontSizes, legacySpace, spacing } from 'theme/theme';
import { showModal } from 'zustand-stores/modalStore';
import { useUserEnvironment } from 'zustand-stores/userEnvironmentStore';

function userAdminRequest(
  user: ApiUser | null | undefined,
  org: ApiOrg,
): boolean {
  return Boolean(
    user?.orgAdminRequests &&
      user.orgAdminRequests.some((o) => o.orgId === org.id),
  );
}

type Props = {
  org: ApiOrg;
  orgOwner: boolean;
};

export function ViewOrgPageSidebarDetails({ org, orgOwner }: Props) {
  const location = useLocation();
  const { user } = useUserEnvironment();
  const openAdminRequest = userAdminRequest(user, org);

  const handleAdminRequest = useCallback(() => {
    if (user) {
      showModal('ADMIN_REQUEST', {
        objectId: org.id,
        objectName: org.name,
      });
    } else {
      showModal('EMAIL_SIGNUP', {
        title:
          /* TRANSLATORS: title of dialog to request to be admin of org */
          getText('Request to Become an Administrator'),
        redirectTo: `${location.pathname}?admin-request=true`,
      });
    }
  }, [user, org, location]);

  return (
    <>
      {!orgOwner && !openAdminRequest && (
        <ClientOnly>
          <ListingViewSidebarInfoBox>
            <Text mb={spacing[8]}>
              {sprintf(getText('Do you work for %(orgName)s?'), {
                orgName: org.name,
              })}
            </Text>

            <Box display="flex" alignItems="center">
              <StyledButtonOrLink
                type="button"
                className="link"
                style={{ fontSize: fontSizes[16] }}
                onClick={handleAdminRequest}
              >
                {getText('Request administrator access')}
              </StyledButtonOrLink>

              <Box ml="4px">
                <Icon name="arrow-right" size={18} colorName="brandBlue" />
              </Box>
            </Box>
          </ListingViewSidebarInfoBox>
        </ClientOnly>
      )}
      {openAdminRequest && (
        <ClientOnly>
          <Text color="lightContentGrey" mb={legacySpace[3]}>
            {sprintf(
              getText(
                'You have a pending request to become an administrator for %(orgName)s',
              ),
              {
                orgName: org.name,
              },
            )}
          </Text>
        </ClientOnly>
      )}
    </>
  );
}
